import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Form, Input, message, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useLocalSelector } from '@app/services/hooks/useRedux';

import { useEdoProfileQuery, useUpdateEdoProfileMutation } from '../../../../hooks';

const PARTICIPANT_ID_MAX_LENGTH = 46;
const PARTICIPANT_ID_PATTERN = /^[a-zA-Z0-9]{3}.{1,43}$/;

interface IFormValues {
  fns_participant_id: string;
}

interface IProps {
  legalEntityUid: string;
}

export const EdoProfile: React.FC<IProps> = ({ legalEntityUid }) => {
  const scope = useLocalSelector((state) => state.currentSession.data?.scope);
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const getErrorMessage = useApiErrorMessage();
  const [form] = Form.useForm<IFormValues>();
  const [editing, setEditing] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string>();
  const profile = useEdoProfileQuery(legalEntityUid);
  const update = useUpdateEdoProfileMutation(legalEntityUid);
  const canUpdate = !!scope?.$permissions?.update;
  const canEdit = canUpdate && !scope?.is_sandbox;

  const startEditing = () => {
    setSaveError(undefined);
    setEditing(true);
  };

  const handleSubmit = (values: IFormValues) => {
    setSaveError(undefined);
    update.mutate(
      { fns_participant_id: (values.fns_participant_id || '').trim() || null },
      {
        onSuccess: () => {
          message.success(commonT.t('dataPatchSuccess'));
          setEditing(false);
        },
        onError: (err) => {
          const { pointerErrors } = getFormErrorsFromResponse(values, err, validationT.t);
          if (pointerErrors.length) {
            form.setFields(pointerErrors);
          } else {
            setSaveError(getErrorMessage(err));
          }
        },
      }
    );
  };

  const renderContent = () => {
    if (profile.isLoading) {
      return <Skeleton.Input active />;
    }
    if (profile.isError) {
      return (
        <Alert
          type="error"
          showIcon
          title={getErrorMessage(profile.error)}
          action={
            <Button size="small" onClick={() => profile.refetch()}>
              {commonT.t('buttonReload')}
            </Button>
          }
        />
      );
    }
    if (editing) {
      return (
        <Form<IFormValues>
          form={form}
          layout="vertical"
          initialValues={{ fns_participant_id: profile.data?.fns_participant_id || '' }}
          onFinish={handleSubmit}
          disabled={update.isLoading}
        >
          <Form.Item<IFormValues>
            name="fns_participant_id"
            label={spaceT.t('edo.label')}
            extra={spaceT.t('edo.hint')}
            rules={[
              {
                pattern: PARTICIPANT_ID_PATTERN,
                transform: (value?: string) => value?.trim(),
                message: validationT.t('invalid_edo_participant_id'),
              },
            ]}
          >
            <Input maxLength={PARTICIPANT_ID_MAX_LENGTH} placeholder={spaceT.t('edo.placeholder')} allowClear />
          </Form.Item>
          {saveError && <Alert type="error" showIcon title={saveError} style={{ marginBottom: 16 }} />}
          <Space>
            <Button type="primary" htmlType="submit" loading={update.isLoading}>
              {commonT.t('buttonSave')}
            </Button>
            <Button onClick={() => setEditing(false)}>{commonT.t('buttonCancel')}</Button>
          </Space>
        </Form>
      );
    }

    return (
      <Flex vertical gap={8} align="flex-start">
        <Typography.Text
          strong={!!profile.data?.fns_participant_id}
          type={profile.data?.fns_participant_id ? undefined : 'secondary'}
        >
          {profile.data?.fns_participant_id || spaceT.t('edo.empty')}
        </Typography.Text>
        {canEdit && (
          <Button icon={<EditOutlined />} onClick={startEditing}>
            {commonT.t('edit')}
          </Button>
        )}
        {canUpdate && scope?.is_sandbox && (
          <Typography.Text type="secondary">{spaceT.t('edo.sandbox')}</Typography.Text>
        )}
      </Flex>
    );
  };

  return (
    <Card size="small" title={spaceT.t('edo.title')}>
      {renderContent()}
    </Card>
  );
};
